/**
 * Schedules work after the current frame has breathing room without relying on
 * React Native's legacy interaction queue. The timeout guarantees a task
 * eventually runs even when a continuous gesture keeps the UI busy.
 */

export type IdleTaskOptions = Readonly<{
  timeoutMs?: number
  minimumDelayMs?: number
}>

type IdleCallbackRequest = (callback: () => void, options?: { timeout?: number }) => number
type IdleCallbackCancel = (handle: number) => void

type IdleRuntime = typeof globalThis & {
  requestIdleCallback?: IdleCallbackRequest
  cancelIdleCallback?: IdleCallbackCancel
}

const runtime = globalThis as IdleRuntime

/**
 * Returns a cancellation function. Cancellation is safe before the delay,
 * while idle work is queued, and after the callback has already run.
 */
export function scheduleIdleTask(
  task: () => void,
  { timeoutMs = 1_000, minimumDelayMs = 0 }: IdleTaskOptions = {},
): () => void {
  let cancelled = false
  let delayTimer: ReturnType<typeof setTimeout> | undefined
  let fallbackTimer: ReturnType<typeof setTimeout> | undefined
  let idleHandle: number | undefined
  let cancelIdle: IdleCallbackCancel | undefined

  const run = () => {
    if (!cancelled) task()
  }

  const request = () => {
    if (cancelled) return

    if (typeof runtime.requestIdleCallback === 'function') {
      cancelIdle = runtime.cancelIdleCallback
      idleHandle = runtime.requestIdleCallback(run, { timeout: timeoutMs })
      return
    }

    fallbackTimer = setTimeout(run, 0)
  }

  if (minimumDelayMs > 0) {
    delayTimer = setTimeout(request, minimumDelayMs)
  } else {
    request()
  }

  return () => {
    cancelled = true
    if (delayTimer) clearTimeout(delayTimer)
    if (fallbackTimer) clearTimeout(fallbackTimer)
    if (idleHandle !== undefined) cancelIdle?.(idleHandle)
  }
}
